<template>
    <div class="rd">
        <div class="pagina">
            <header class="paginakop">
                <div class="tekst">
                    <nav class="kruimel" aria-label="Breadcrumb">
                        <router-link to="/experiences">Experiences</router-link>
                        <span>/</span>
                        <span>{{ experience ? experience.name : "Experience" }}</span>
                    </nav>
                    <h1>{{ experience ? experience.name : "Experience" }}</h1>
                    <p v-if="experience">{{ headerSubtitle }}</p>
                </div>
                <div class="rechts">
                    <router-link class="btn btn-stil btn-sm" to="/experiences">All experiences</router-link>
                </div>
            </header>

            <template v-if="!loading">
                <section class="paneel filters">
                    <div class="filter-rij">
                        <div class="veld">
                            <label for="ed-status">Device status</label>
                            <select id="ed-status" v-model="statusId">
                                <option v-for="opt in ACTIVE_OPTIONS" :key="opt.id" :value="opt.id">{{ opt.name }}</option>
                            </select>
                        </div>
                        <div class="veld f-loc">
                            <label for="ed-loc">Location</label>
                            <select id="ed-loc" v-model="locationId">
                                <option value="all">All locations</option>
                                <option v-for="loc in locations" :key="loc.id" :value="loc.id">{{ loc.name }}</option>
                            </select>
                        </div>
                        <div class="veld">
                            <label for="ed-land">Country</label>
                            <select id="ed-land" v-model="countryId">
                                <option value="all">All countries</option>
                                <option v-for="c in sortedCountries" :key="c.id" :value="c.id">{{ c.name }}</option>
                            </select>
                        </div>
                        <div class="veld">
                            <label for="ed-type">Device type</label>
                            <select id="ed-type" v-model="typeId">
                                <option v-for="opt in TYPE_OPTIONS" :key="opt.id" :value="opt.id">{{ opt.name }}</option>
                            </select>
                        </div>
                    </div>
                </section>

                <section class="paneel">
                    <div class="paneel-kop" style="flex-wrap:wrap">
                        <div class="tekst">
                            <h2>Status per X-Cube</h2>
                            <span class="uitleg">On: bookable and playable. Demo: plays a short demo, not bookable. Off: hidden on the X-Cube.</span>
                        </div>
                        <div v-if="tableData.length > 1" class="rechts">
                            <span class="label" style="align-self:center">Set all shown to</span>
                            <button class="btn btn-stil btn-sm" type="button" :disabled="settingAll" @click="setAllShown('on')">On</button>
                            <button class="btn btn-stil btn-sm" type="button" :disabled="settingAll" @click="setAllShown('demo')">Demo</button>
                            <button class="btn btn-stil btn-sm" type="button" :disabled="settingAll" @click="setAllShown('off')">Off</button>
                        </div>
                    </div>

                    <div v-if="tableData.length" class="tabel-wikkel">
                        <table class="tabel">
                            <thead>
                                <tr><th>X-Cube</th><th>Status</th></tr>
                            </thead>
                            <tbody>
                                <tr v-for="row in tableData" :key="row.id">
                                    <td>
                                        <div class="twee">
                                            <span>{{ row.location.name }}</span>
                                            <small>{{ row.name }}</small>
                                        </div>
                                    </td>
                                    <td>
                                        <div class="segment klein" role="radiogroup" :aria-label="`Status for ${row.name}`">
                                            <button
                                                v-for="opt in STATUS_OPTIONS"
                                                :key="opt.value"
                                                type="button"
                                                role="radio"
                                                :aria-checked="row.status === opt.value"
                                                @click="changeStatus(row, opt.value)"
                                            >{{ opt.label }}</button>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div v-else class="leeg">
                        <h3>No X-Cube matches these filters</h3>
                        <p>Clear a filter or set Device status to All.</p>
                    </div>
                </section>
            </template>

            <div v-else class="leeg"><h2>Loading experience…</h2></div>
        </div>
    </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router/composables";
import {
    createXCubeExperienceStatus,
    getExperienceSimple,
    getXCubeExperiences,
    updateXCubeExperienceStatus,
} from "../../api/graphql/experiences";
import { getUserLocationsSimple } from "../../api/graphql/locations";
import { getXCubesSimple } from "../../api/graphql/xcubes";
import { Message } from "element-ui";

const ALL_OPTION = { name: "All", id: "all" };

const ACTIVE_OPTIONS = [
    ALL_OPTION,
    { name: "Active", id: "active" },
    { name: "Inactive", id: "inactive" },
];

const TYPE_OPTIONS = [
    { name: "XCube", id: "xcube" },
    { name: "XPod", id: "xpod" },
    { name: "Kiosk", id: "xkiosk" },
    { name: "Xtend", id: "xtend" },
    ALL_OPTION,
];

const STATUS_OPTIONS = [
    { value: "on", label: "On" },
    { value: "demo", label: "Demo" },
    { value: "off", label: "Off" },
];

const route = useRoute();
const experience = ref(null);
const xCubeExperiences = ref([]);
const allXCubes = ref([]);
const loading = ref(true);
const locations = ref([]);
const settingAll = ref(false);

// Device status/type default to the same scope as the old XCubesFilter (Active, XCube).
const filter = ref({
    active: { name: "Active", id: "active" },
    location: ALL_OPTION,
    type: { name: "XCube", id: "xcube" },
    country: ALL_OPTION,
});

const headerSubtitle = computed(() => {
    const e = experience.value;
    if (!e) {
        return "";
    }
    const parts = [];
    if (e.duration) {
        parts.push(`${e.duration} min`);
    }
    if (e.min_number_of_players && e.max_number_of_players) {
        parts.push(`${e.min_number_of_players}–${e.max_number_of_players} players`);
    }
    if (e.code) {
        parts.push(e.code);
    }
    return parts.join(" · ");
});

const sortedCountries = computed(() => {
    const set = locations.value.reduce((acc, loc) => {
        if (loc.country) {
            acc.add(loc.country.toUpperCase());
        }
        return acc;
    }, new Set());
    return Array.from(set)
        .sort((a, b) => a.localeCompare(b))
        .map((c) => ({ name: c, id: c }));
});

// Native <select> works with string values; map each to the {name,id} object the filter holds.
const statusId = computed({
    get: () => filter.value.active?.id ?? "all",
    set: (id) => {
        filter.value.active = ACTIVE_OPTIONS.find((o) => o.id === id) ?? ALL_OPTION;
    },
});
const typeId = computed({
    get: () => filter.value.type?.id ?? "all",
    set: (id) => {
        filter.value.type = TYPE_OPTIONS.find((o) => o.id === id) ?? ALL_OPTION;
    },
});
const locationId = computed({
    get: () => filter.value.location?.id ?? "all",
    set: (id) => {
        filter.value.location =
            id === "all" ? ALL_OPTION : locations.value.find((l) => String(l.id) === String(id)) ?? ALL_OPTION;
    },
});
const countryId = computed({
    get: () => filter.value.country?.id ?? "all",
    set: (id) => {
        filter.value.country = id === "all" ? ALL_OPTION : { name: id, id };
    },
});

const tableData = computed(() => {
    if (!allXCubes.value || !xCubeExperiences.value) {
        return [];
    }
    return allXCubes.value.map((xcube) => {
        const xCubeExperience = xCubeExperiences.value.find(
            (item) => item.xcube?.id === xcube.id,
        );
        return {
            ...xcube,
            xCubeExperienceId: xCubeExperience ? xCubeExperience.id : null,
            status: xCubeExperience ? xCubeExperience.status : "off",
        };
    });
});

const cleanedFilter = computed(() => {
    const value = {};
    if (filter.value.active?.id && filter.value.active.id !== ALL_OPTION.id) {
        value.status = filter.value.active.id;
    }
    if (filter.value.location?.id && filter.value.location.id !== ALL_OPTION.id) {
        value.location = filter.value.location.id;
    }
    if (filter.value.type?.id && filter.value.type.id !== ALL_OPTION.id) {
        value.type = filter.value.type.id;
    }
    if (filter.value.country?.id && filter.value.country.id !== ALL_OPTION.id) {
        value.country = filter.value.country.id;
    }
    return value;
});

const getFilteredDevices = async () => getXCubesSimple(cleanedFilter.value);

async function applyStatus(row, newStatus) {
    if (row.xCubeExperienceId) {
        await updateXCubeExperienceStatus(row.xCubeExperienceId, newStatus);
    } else {
        await createXCubeExperienceStatus(row.id, experience.value.id, newStatus);
    }
}

const changeStatus = async (row, newStatus) => {
    if (row.status === newStatus) {
        return;
    }
    try {
        await applyStatus(row, newStatus);
        xCubeExperiences.value = await getXCubeExperiences(route.params.id);
        Message.success(`${experience.value.name} set to ${newStatus} on ${row.name}`);
    } catch (e) {
        Message.error(e instanceof Error ? e.message : `Could not change status for ${row.name}`);
    }
};

// Apply one status to every row currently shown, sequentially (no Promise.all over many mutations).
const setAllShown = async (newStatus) => {
    const rows = tableData.value.filter((row) => row.status !== newStatus);
    if (!rows.length) {
        return;
    }
    settingAll.value = true;
    try {
        for (const row of rows) {
            await applyStatus(row, newStatus);
        }
        xCubeExperiences.value = await getXCubeExperiences(route.params.id);
        Message.success(`${experience.value.name} set to ${newStatus} on ${rows.length} X-Cubes`);
    } catch (e) {
        xCubeExperiences.value = await getXCubeExperiences(route.params.id);
        Message.error(e instanceof Error ? e.message : "Could not update all X-Cubes");
    } finally {
        settingAll.value = false;
    }
};

const refreshDevices = async () => {
    loading.value = true;
    allXCubes.value = await getFilteredDevices();
    loading.value = false;
};

onMounted(async () => {
    loading.value = true;
    const result = await Promise.all([
        getExperienceSimple(route.params.id),
        getFilteredDevices(),
        getXCubeExperiences(route.params.id),
        getUserLocationsSimple(),
    ]);
    [experience.value, allXCubes.value, xCubeExperiences.value, locations.value] = result;
    loading.value = false;
});

watch(filter, refreshDevices, { deep: true });
</script>
